import { Box, Divider, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InstantSearch } from 'react-instantsearch';
import { Provider, useAtomValue, useSetAtom } from 'jotai';
import { Business_Profile_Enum, User_Role_Enum } from '@kheops/graphql';
import { PageWrapper } from '../../common/components/PageWrapper';
import { useSearchClient } from '../../hooks/useSearchClient';
import SearchPagination from '../SearchPagination';
import QueryTag from '../filters/QueryTag';
import GeoLocationTags from '../filters/GeoLocationTags';
import SearchConfigure from '../SearchConfigure';
import { authStateAtom } from '../../common/state/state';
import { HistoryIndexUiState, OnStateChangeArgs, currentExpandedAccordionAtom } from '../state/state';
import FullTextSearch from '../../common/components/FullTextSearch';
import FilterUtils from '../filters/filter.utils';
import { DiscoveryWordingsKeys } from '../discovery.utils';
import useBaseStore from '../../hooks/useBaseStore';
import { currentContextAtom } from '../../state';
import ClearFiltersButton from '../filters/ClearFiltersButton';
import useDiscoveryDefaultFilters from '../../hooks/useDiscoveryDefaultFilters';
import useUpdateUiState from '../../discovery/hooks/useUpdateHistoryUiState';
import BrandTags from '../filters/BrandTags';
import MyScopeFilter from '../filters/MyScopeFilter';
import MyDeliveryZonesFilter from '../filters/MyDeliveryZonesFilter';
import useDiscoveryDefaultRefinements from '../../hooks/useDiscoveryDefaultRefinements';
import SearchBuyerFilterChip from './SearchBuyerFilterChip';
import SearchBuyerCounts from './SearchBuyerCount';
import SearchBuyerHits from './SearchBuyerHits';

const SEARCH_BUYER_PAGE_HITS_PER_PAGE = 60;

export default function SearchBuyerPage(): React.JSX.Element {
  const theme = useTheme();
  const { t } = useTranslation(['discovery', 'common']);
  const index = import.meta.env.REACT_APP_ALGOLIA_COMPANY_INDEX;
  const { companyGeoloc, companyId, deliveryZones, userBrands, userAdministrativeAreas, realm, roleName } = useAtomValue(currentContextAtom);
  const authInfo = useAtomValue(authStateAtom);
  const searchClient = useSearchClient();
  const baseStore = useBaseStore();
  const [fullTextSearchValue, setFullTextSearchValue] = useState((window.history.state.uiState?.[index] as HistoryIndexUiState)?.query || '');
  const setCurrentExpandedAccordion = useSetAtom(currentExpandedAccordionAtom);
  const { initialIndexUiState, updateUiState } = useUpdateUiState({ index: import.meta.env.REACT_APP_ALGOLIA_COMPANY_INDEX });
  const defaultFilters = useDiscoveryDefaultFilters();
  const filters = `${defaultFilters} AND ${FilterUtils.GetSignedContractFilter('partners', companyId)}`;
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const handleStateChange = (args: OnStateChangeArgs): void => {
    updateUiState(args);
    setFullTextSearchValue(args.uiState[index].query || '');
  };

  const isScopeFilterDisplayed = realm === Business_Profile_Enum.Supplier
      && [User_Role_Enum.SupplierSalesAdmin, User_Role_Enum.SupplierSalesRep].includes(roleName)
      && (
        (!!userBrands?.length && !userBrands[0]?.has_all_brands)
        || (!!userAdministrativeAreas?.length && !userAdministrativeAreas[0].has_all_areas)
      );

  const isDeliveryFilterDisplayed = !!deliveryZones?.length && !deliveryZones?.some((zone) => zone.administrative_areas?.some(({ area }) => area === 'ALL_AREAS'));

  const defaultRefinements = useDiscoveryDefaultRefinements({ initialIndexUiState, isScopeFilterDisplayed, isDeliveryFilterDisplayed });

  useEffect(() => {
    setCurrentExpandedAccordion('geolocation');
  }, []);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {!!companyGeoloc && authInfo.isAuthenticated && (
        <Provider store={baseStore}>
          <PageWrapper
            sx={{
              flex: 1,
              pt: 2,
            }}
          >
            <InstantSearch
              indexName={index}
              searchClient={searchClient}
              initialUiState={{
                [index]: initialIndexUiState || defaultRefinements,
              }}
              onStateChange={handleStateChange}
            >
              <SearchConfigure filters={filters} index={index} hitsPerPage={SEARCH_BUYER_PAGE_HITS_PER_PAGE}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'start',
                  }}
                >
                  <Typography
                    variant={isXs ? 'headlineSmall' : 'headlineMedium'}
                    sx={{
                      paddingBottom: 2,
                      textAlign: 'center',
                    }}
                  >
                    {t(DiscoveryWordingsKeys.SUPPLIER.header)}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: {
                        xs: 'column',
                        lg: 'row',
                      },
                      gap: 1,
                      width: '100%',
                    }}
                  >
                    <FullTextSearch
                      label={t(DiscoveryWordingsKeys.SUPPLIER.search_by_name)}
                      inputProps={{
                        placeholder: t('discovery:search_buyers'),
                        sx: {
                          height: 48,
                          width: {
                            xs: '100%',
                            lg: 420,
                          },
                        },
                      }}
                      value={fullTextSearchValue}
                    />

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: {
                          xs: 'column',
                          sm: 'row',
                        },
                        flex: 1,
                        gap: 1,
                        alignItems: {
                          sm: 'center',
                        },
                        '& > .MuiDivider-root': {
                          mx: 0.5,
                        },
                      }}
                    >
                      <Divider
                        orientation="vertical"
                        sx={{
                          height: '24px',
                          display: {
                            xs: 'none',
                            lg: 'flex',
                          },
                        }}
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {isScopeFilterDisplayed && (<MyScopeFilter />)}
                        {isDeliveryFilterDisplayed && (<MyDeliveryZonesFilter />)}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: {
                            sm: 'space-between',
                          },
                          flex: 1,
                          flexWrap: 'wrap',
                          gap: 1,
                          alignItems: 'center',
                        }}
                      >
                        <SearchBuyerFilterChip />
                        <SearchBuyerCounts />
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      pt: 1.5,
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <GeoLocationTags />
                    <BrandTags />
                    <QueryTag />
                    <ClearFiltersButton
                      attributesToCheck={[
                        'product_families',
                        'administrative_areas',
                      ]}
                    />
                  </Box>
                </Box>
                <Box
                  sx={{
                    py: 2,
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    justifyContent: 'start',
                  }}
                >
                  <SearchBuyerHits />
                  <SearchPagination />
                </Box>
              </SearchConfigure>
            </InstantSearch>
          </PageWrapper>
        </Provider>
      )}
    </>
  );
}
